import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/axiosClient';
import HeroSection from './HeroSection';
import PredefinedSearchSection from './PredefinedSearchSection';
import TrainersSection from './TrainersSection';
import StatisticsSection from './StatisticsSection';
import TestimonialsSection from './TestimonialsSection';
import FAQSection from './FAQSection';
import CTASection from './CTASection';
import SeoHead from './SeoHead';
import SeoDescriptionSection from './SeoDescriptionSection';
import CustomCssSection from './CustomCssSection';
import PortalBannersSection from './PortalBannersSection';
import { LandingPage as LandingPageType } from '../../types/landingPage';
import '../../assets/css/landing-page.css';

const LandingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [landingPage, setLandingPage] = useState<LandingPageType | null>(null);
  const [trainers, setTrainers] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLandingPageData = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        // Fetch landing page data
        const landingResponse = await api.get(`/landing/${slug}/`);
        setLandingPage(landingResponse.data);

        // Fetch trainers with specific filters for this landing page
        const trainersResponse = await api.get('/ads-preview/', {
          params: {
            minPrice: landingResponse.data.trainers_min_price || 50
          }
        });
        const allTrainers = trainersResponse.data || [];

        // Filter trainers that match ALL criteria from landing page
        const filteredTrainers = allTrainers.filter((trainer: any) => {
          const locationMatch = trainer.location?.toLowerCase().trim() === landingResponse.data.location_name?.toLowerCase().trim();
          const categoryMatch = trainer.sub_category?.toLowerCase().trim() === landingResponse.data.subcategory_name?.toLowerCase().trim();

          return locationMatch && categoryMatch;
        });

        // Sort by review count (descending)
        const sortedTrainers = filteredTrainers.sort((a: any, b: any) => {
          return b.rating.review_count - a.rating.review_count;
        });

        setTrainers(sortedTrainers);

        // Fetch reviews for top 8 displayed trainers only
        try {
          const topTrainers = sortedTrainers.slice(0, 8);
          const trainerIds = topTrainers.map((trainer: any) => trainer.user_id).join(',');

          const reviewsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}trainer-reviews/?trainer_ids=${trainerIds}`);
          const reviewsData = await reviewsResponse.json();

          if (reviewsData && reviewsData.reviews) {
            // Flatten all reviews from different trainers
            const allReviews: any[] = [];
            Object.values(reviewsData.reviews).forEach((trainerReviews: any) => {
              if (Array.isArray(trainerReviews)) {
                allReviews.push(...trainerReviews);
              }
            });
            setReviews(allReviews);
          } else {
            setReviews([]);
          }
        } catch (err) {
          console.warn('Failed to fetch reviews:', err);
          setReviews([]);
        }
      } catch (err) {
        console.error('Error fetching landing page:', err);
        setError('Nie udało się załadować strony');
      } finally {
        setLoading(false);
      }
    };

    fetchLandingPageData();
  }, [slug]);

  if (loading) {
    return (
      <div className="landing-loading">
        <div className="landing-spinner"></div>
      </div>
    );
  }

  if (error || !landingPage) {
    return (
      <div className="landing-error">
        <div className="landing-error-content">
          <h1>Strona nie została znaleziona</h1>
          <p>Przepraszamy, ale ta strona nie istnieje.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SeoHead landingPage={landingPage} />
      <CustomCssSection
        showCustomCss={landingPage.show_custom_css}
        customCss={landingPage.custom_css}
      />
      <div className="landing-page">
        <HeroSection
          title={landingPage.title}
          titleProcessed={landingPage.hero_title_processed}
          heroFeaturedImage={landingPage.hero_featured_image}
          heroSubtitle={landingPage.hero_subtitle}
          heroPrimaryBtnText={landingPage.hero_primary_btn_text}
          heroPrimaryBtnUrl={landingPage.hero_primary_btn_url}
          heroSecondaryBtnText={landingPage.hero_secondary_btn_text}
          heroSecondaryBtnUrl={landingPage.hero_secondary_btn_url}
          heroBadgeText={landingPage.hero_badge_text}
        />
        <PredefinedSearchSection
          location={landingPage.location_name}
          location_id={landingPage.location_id}
          category={landingPage.category_name}
          category_id={landingPage.category_id}
          subcategory={landingPage.subcategory_name}
          subcategory_id={landingPage.subcategory_id}
        />
        {landingPage.show_seo_description && (
          <>
            {/* SEO Steps section */}
            {landingPage.seo_description_steps_html && landingPage.seo_description_steps_html.trim() && (
              <div
                dangerouslySetInnerHTML={{ __html: landingPage.seo_description_steps_html }}
              />
            )}

            {/* Portal banners between SEO sections */}
            {landingPage.portal_banners && landingPage.portal_banners.length > 0 && (
              <PortalBannersSection
                banners={landingPage.portal_banners}
                title={landingPage.portal_banners_title}
                subtitle={landingPage.portal_banners_subtitle}
              />
            )}

            {/* SEO Benefits section */}
            {landingPage.seo_description_benefits_html && landingPage.seo_description_benefits_html.trim() && (
              <div
                dangerouslySetInnerHTML={{ __html: landingPage.seo_description_benefits_html }}
              />
            )}
          </>
        )}
        {landingPage.show_trainers && (
          <TrainersSection
            trainers={trainers}
            location={landingPage.location_name}
            category={landingPage.category_name}
            showTrainers={landingPage.show_trainers}
            trainersTitle={landingPage.trainers_title}
            trainersSubtitle={landingPage.trainers_subtitle_processed}
            trainersLimit={landingPage.trainers_limit}
            trainersButtonText={landingPage.trainers_button_text}
            trainersSeeAllText={landingPage.trainers_see_all_text}
            trainersCountText={landingPage.trainers_count}
            trainersBadgeText={landingPage.trainers_badge_text_processed}
            trainersHighlightText={landingPage.trainers_highlight_text_processed}
            trainersCtaSubtitle={landingPage.trainers_cta_subtitle}
            trainersStat1Number={landingPage.trainers_stat1_number}
            trainersStat1Label={landingPage.trainers_stat1_label}
            trainersStat2Number={landingPage.trainers_stat2_number}
            trainersStat2Label={landingPage.trainers_stat2_label}
            trainersStat3Number={landingPage.trainers_stat3_number}
            trainersStat3Label={landingPage.trainers_stat3_label}
          />
        )}
        {landingPage.show_statistics && (
          <StatisticsSection
            title={landingPage.statistics_title}
            statistics={landingPage.statistics}
          />
        )}
        {landingPage.show_testimonials && (
          <TestimonialsSection
            trainers={trainers}
            reviews={reviews}
            title={landingPage.testimonials_title}
            subtitle={landingPage.testimonials_subtitle_processed}
            defaultContent={landingPage.default_testimonial_content}
            defaultResult={landingPage.default_testimonial_result}
          />
        )}
        {landingPage.show_faq && <FAQSection faqs={landingPage.faqs} title={landingPage.faq_title} subtitle={landingPage.faq_subtitle} />}
        {landingPage.show_cta_section && (
          <CTASection
            showCtaSection={landingPage.show_cta_section}
            title={landingPage.cta_title}
            subtitle={landingPage.cta_subtitle_processed}
            primaryBtnText={landingPage.cta_primary_btn_text}
            primaryBtnUrl={landingPage.cta_primary_btn_url}
            secondaryBtnText={landingPage.cta_secondary_btn_text_processed}
            secondaryBtnUrl={landingPage.cta_secondary_btn_url}
            stat1Number={landingPage.cta_stat1_number}
            stat1Label={landingPage.cta_stat1_label_processed}
            stat2Number={landingPage.cta_stat2_number}
            stat2Label={landingPage.cta_stat2_label}
            stat3Number={landingPage.cta_stat3_number}
            stat3Label={landingPage.cta_stat3_label}
          />
        )}
      </div>
    </>
  );
};

export default LandingPage;